import React, { useMemo } from 'react';
import { ArrayTooltipContent, Column, Row, Title } from '@ydistri/ds';
import { ALIGN_SELF_CENTER } from '../categoryInsightsLib';
import BarSourceByReason from './BarSourceByReason';
import BarSourceByInventoryType from './BarSourceByInventoryType';
import BarTargetByInventoryType from './BarTargetByInventoryType';
import BarTargetByReason from './BarTargetByReason';
import useCategoryId from '../../../../hooks/useCategoryId';
import { GetCategoryInsightsParams, useGetCategoryInsightsQuery } from '../apiCategoryInsights';
import { shortenNumber } from '@ydistri/generic-utils';
import { Popover } from 'antd';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';
import { skipToken } from '@reduxjs/toolkit/query';

const RedistributionAnalysis: React.FC = () => {
  const categoryId = useCategoryId();
  const currency = useCurrency();
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const categoryInsightsParam: GetCategoryInsightsParams = {
    categoryId,
    templateOrCalculation,
  };

  const { data: categoryData, isLoading: isCategoryInsightsLoading } = useGetCategoryInsightsQuery(
    templateOrCalculation.hasValidValue ? categoryInsightsParam : skipToken,
  );

  const redistributionData = useMemo(
    () => categoryData?.redistributions,
    [categoryData?.redistributions],
  );

  const finalRedistributionTooltipData = useMemo(
    () => [
      {
        title: 'Final redistribution',
        value: redistributionData?.total ?? 0,
        postfix: currency,
      },
    ],
    [redistributionData?.total, currency],
  );

  const tooltipContent = useMemo(
    () => <ArrayTooltipContent data={finalRedistributionTooltipData} />,
    [finalRedistributionTooltipData],
  );

  if (!redistributionData || isCategoryInsightsLoading) {
    return null;
  }

  return (
    <Row $gap="5rem" $flexGrow={1} $justifyContent="center" $flexWrap="wrap">
      <Row $gap="5rem" $justifyContent="center">
        <BarSourceByReason data={redistributionData} />
        <BarSourceByInventoryType data={redistributionData} />
      </Row>
      <Column $alignItems="center" $justifyContent="center">
        <Title $size="xLarge" style={ALIGN_SELF_CENTER}>
          Final redistribution
        </Title>
        <Popover content={tooltipContent}>
          <Title $size="xxLarge" style={ALIGN_SELF_CENTER}>
            {shortenNumber(redistributionData.total)} {currency}
          </Title>
        </Popover>
      </Column>
      <Row $gap="5rem" $justifyContent="center">
        <BarTargetByInventoryType data={redistributionData} />
        <BarTargetByReason data={redistributionData} />
      </Row>
    </Row>
  );
};

export default RedistributionAnalysis;
